import Vue from 'vue'
import { addNamespace } from './utils'
import LessonsApi from '@/apis/lessons'
import { getProperty } from '@/utils/object'

const lessonsApi = new LessonsApi()

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{lessonDetails: Object<Number, Object>, understood: Object<number, LessonUnderstood>, errors: Object<number, String>}} LessonStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const LessonStoreTypes = {
  getters: {
    GET_LESSON: 'getLesson',
    GET_LESSON_ERROR: 'getLessonError',
    GET_UNDERSTOOD_STATUS: 'getUnderstoodStatus',
    HAS_5_SHARP_TEST: 'has5sharpTest',
  },
  actions: {
    FETCH_LESSON: 'fetchLesson',
    SUBMIT_UNDERSTOOD_STATUS: 'submitUnderstoodStatus',
  },
  mutations: {
    CLEAR_ERROR: 'clearError',
    SET_LESSON: 'setLesson',
    SET_LESSON_ERROR: 'setLessonError',
    SET_UNDERSTOOD_STATUS: 'setUnderstoodStatus',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const LessonStoreNamespacedTypes = addNamespace(
  'lessons',
  LessonStoreTypes
)

/**
 * @returns {LessonStoreState}
 */
export function state() {
  return { lessonDetails: {}, errors: {}, understood: {} }
}

export const getters = {
  /**
   * @param {LessonStoreState} state
   * @returns {function(number):Object}
   */
  [LessonStoreTypes.getters.GET_LESSON]: state => id => {
    return state.lessonDetails[id] || null
  },

  /**
   * @param {LessonStoreState} state
   * @returns {function(number):String}
   */
  [LessonStoreTypes.getters.GET_LESSON_ERROR]: state => lessonId => {
    return state.errors[lessonId] || ''
  },

  /**
   * @param {BookStoreState} state
   * @returns {function(Number):LessonUnderstood|null}
   */
  [LessonStoreTypes.getters.GET_UNDERSTOOD_STATUS]: state => id => {
    return state.understood[id] || null
  },

  /**
   * @param {LessonStoreState} state
   * @returns {function(number):Object}
   */
  [LessonStoreTypes.getters.HAS_5_SHARP_TEST]: (state, getters) => id => {
    return getProperty(
      getters[LessonStoreTypes.getters.GET_LESSON](id),
      'has5SharpTest',
      false
    )
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} lessonId
   */
  [LessonStoreTypes.actions.FETCH_LESSON]({ commit, getters }, lessonId) {
    if (getters[LessonStoreTypes.getters.GET_LESSON](lessonId) !== null) {
      return
    }

    commit(LessonStoreTypes.mutations.CLEAR_ERROR, lessonId)

    lessonsApi
      .getLesson(lessonId)
      .then(lessonDetails => {
        commit(LessonStoreTypes.mutations.SET_LESSON, {
          lessonId,
          lessonDetails,
        })
      })
      .catch(({ message }) => {
        commit(LessonStoreTypes.mutations.SET_LESSON_ERROR, {
          lessonId,
          error: message,
        })
      })
  },

  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {Number} lessonId
   * @param {LessonUnderstood} understood
   * @returns {Promise}
   */
  [LessonStoreTypes.actions.SUBMIT_UNDERSTOOD_STATUS](
    { commit },
    { lessonId, understood }
  ) {
    return lessonsApi
      .setUnderstood(understood, lessonId)
      .then(() => {
        commit(LessonStoreTypes.mutations.SET_UNDERSTOOD_STATUS, {
          lessonId,
          understood,
        })
      })
      .catch(({ message }) => {
        commit(LessonStoreTypes.mutations.ADD_ERROR, message)
      })
  },
}

export const mutations = {
  /**
   * @param {LessonStoreState} state
   * @param {number} lessonId
   */
  [LessonStoreTypes.mutations.CLEAR_ERROR](state, lessonId) {
    Vue.delete(state.errors, lessonId)
  },

  /**
   * @param {LessonStoreState} state
   * @param {number} lessonId
   * @param {Object} lessonDetails
   */
  [LessonStoreTypes.mutations.SET_LESSON](state, { lessonId, lessonDetails }) {
    Vue.set(state.lessonDetails, lessonId, lessonDetails)
  },

  /**
   * @param {LessonStoreState} state
   * @param {Number} lessonId
   * @param {LessonUnderstood} understood
   */
  [LessonStoreTypes.mutations.SET_UNDERSTOOD_STATUS](
    state,
    { understood, lessonId }
  ) {
    Vue.set(state.understood, lessonId, understood)
  },

  /**
   * @param {LessonStoreState} state
   * @param {Number} lessonId
   * @param {Object} error
   */
  [LessonStoreTypes.mutations.SET_LESSON_ERROR](state, { lessonId, error }) {
    Vue.set(state.errors, lessonId, error)
  },
}

export default {
  namespaced: true,
  Types: LessonStoreTypes,
  NamespacedTypes: LessonStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
